/* Sequential awaits / continue / for-of are intentional: the voice call loop must run one step at a time. */
/* eslint-disable no-await-in-loop, no-continue, no-restricted-syntax */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import FeatherIcon from 'feather-icons-react';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { jsPDF as JsPDF } from 'jspdf';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Scrollbars } from 'react-custom-scrollbars';
import { Drawer, message, Skeleton, Tooltip } from 'antd';
import { createListener, playBlob, toSpokenText, createDirectVoiceApi } from './genieVoice';
import { localReply, normalizeCrm, rememberReply, resetSmallTalk } from './genieBrain';
import { setChatCall } from '../../../../redux/CrmReduxCalls/EditNavCtrl';
import ConfirmationPopup from '../../Popup/ConfirmationPopup'
import { Footer } from '../../style'
import { CancelIcon, ClearChatIcon } from '../../../../components/Icons/Icons';
import { Button } from '../../../../components/buttons/buttons';
import { getSocket } from "../../../../Api/Socket";
import * as ChatApi from "../../../../Api/Services/ChatbotapiService"
import './ChatBot.css';

const { fetchLastchatHistory, Userchatsubmit, genieChatDelte, genieChatClear } = ChatApi;

/**
 * Voice calls use Deepgram straight from the browser. The key and options come from the app's environment
 * (set separately from this file):
 *   REACT_APP_DEEPGRAM_API_KEY      required for live voice; without it the call screen plays the scripted demo
 *   REACT_APP_DEEPGRAM_TTS_MODEL    optional, default aura-2-apollo-en (male voice)
 *   REACT_APP_GENIE_VOICE_SPEED     optional, 1 = normal, default 1.2
 */
const DG_KEY = process.env.REACT_APP_DEEPGRAM_API_KEY;
const VOICE_SPEED = parseFloat(process.env.REACT_APP_GENIE_VOICE_SPEED) || 1.2;
const VOICE_API = DG_KEY
    ? createDirectVoiceApi(DG_KEY, { ttsModel: process.env.REACT_APP_DEEPGRAM_TTS_MODEL || 'aura-2-apollo-en' })
    : null;

/**
 * Suggestion chips. To load them from the backend instead, first add this export to ChatbotapiService:
 *   export const fetchSuggestedQuestions = (companyCode) => api.get(`/chat/suggestions?companyCode=${companyCode}`)
 * then call it in fetchAll() and pass the result to setSuggestions.
 */
const FALLBACK_SUGGESTIONS = [
    { id: 'priorities', category: 'Priorities', question: 'What should I work on today?' },
    { id: 'hot-leads', category: 'Leads', question: 'Show me my hot leads' },
    { id: 'stalled', category: 'Pipeline', question: 'Which deals have not moved in 30 days?' },
];

const PREVIEW_STYLES = `
<style>
  body{font-family:Inter,Segoe UI,Arial,sans-serif;font-size:13px;line-height:1.55;color:#1f2937;margin:0;padding:14px}
  table{border-collapse:collapse;width:100%;margin:8px 0}
  th,td{border:1px solid #e5e7eb;padding:6px 10px;text-align:left}
  th{background:#f3f4f6;font-weight:600}
  h1,h2,h3,h4{margin:10px 0 6px;color:#111827}
  ul,ol{padding-left:20px}
  a{color:#2563eb}
</style>`;

const cleanHtml = (html) => DOMPurify.sanitize(html || '', { USE_PROFILES: { html: true } });

/**
 * Turns a response's HTML into simple blocks (heading / paragraph / list item / table)
 * so the PDF can be written as real text instead of a screenshot.
 */
const blocksFromHtml = (html) => {
    const doc = new DOMParser().parseFromString(cleanHtml(html), 'text/html');
    const blocks = [];
    const txt = (el) => (el.textContent || '').replace(/\s+/g, ' ').trim();
    const BLOCK = 'P,DIV,UL,OL,TABLE,H1,H2,H3,H4,H5,H6,LI,PRE,BLOCKQUOTE';
    const walk = (node) => {
        node.childNodes.forEach((n) => {
            if (n.nodeType === 3) {
                const t = n.textContent.replace(/\s+/g, ' ').trim();
                if (t) blocks.push({ type: 'p', text: t });
                return;
            }
            if (n.nodeType !== 1) return;
            const tag = n.tagName;
            if (tag === 'BR' || tag === 'STYLE' || tag === 'SCRIPT') return;
            if (/^H[1-6]$/.test(tag)) {
                blocks.push({ type: 'h', text: txt(n), level: Number(tag[1]) });
            } else if (tag === 'UL' || tag === 'OL') {
                [...n.children].filter(c => c.tagName === 'LI').forEach((li, i) => {
                    blocks.push({ type: 'li', text: `${tag === 'OL' ? `${i + 1}.` : '•'} ${txt(li)}` });
                });
            } else if (tag === 'TABLE') {
                const rows = [...n.querySelectorAll('tr')].map(tr => [...tr.children].map(txt));
                blocks.push({ type: 'table', rows, header: !!n.querySelector('th') });
            } else if (n.querySelector(BLOCK)) {
                walk(n);
            } else {
                const t = txt(n);
                if (t) blocks.push({ type: 'p', text: t });
            }
        });
    };
    walk(doc.body);
    return blocks;
};

const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
};

/**
 * Sample conversation used when the voice backend is not connected (visual preview only, no audio).
 * who: 'g' = Genie speaks, 'u' = the user speaks.
 */
const CALL_SCRIPT = [
    { who: 'g', text: "Hello! I'm Genie. What can I help you with today?" },
    { who: 'u', text: 'Which deals should I focus on this week?' },
    { who: 'g', text: 'Three deals need you. Northwind renewal is in negotiation and asked for revised terms on Monday. Acme has a proposal worth one hundred twenty thousand. And Globex looks like a good upsell.' },
    { who: 'u', text: 'Draft a follow-up for Northwind.' },
    { who: 'g', text: "Done. I drafted a short email confirming the revised terms and suggesting a call on Thursday. Want me to log this call on the deal?" },
];

const SPARKLE = <svg viewBox="0 0 24 24"><path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" /></svg>;

/**
 * Call screen. Live mode: mic -> Deepgram speech-to-text -> your chat pipeline -> Deepgram text-to-speech.
 * Demo mode (no voiceApi): plays CALL_SCRIPT.
 *   ask(text)  submits a question exactly like typing it in the chat
 *   latest     the newest finished answer in the chat list ({ chatHistoryId, response })
 */
function GenieCall({ onEnd, firstName, voiceApi, ask, latest }) {
    const live = !!(voiceApi && voiceApi.transcribe && voiceApi.speak);
    const [state, setState] = useState('t');   // g = speaking, u = listening, t = thinking
    const [lines, setLines] = useState([]);
    const [secs, setSecs] = useState(0);
    const [muted, setMuted] = useState(false);
    const [notice, setNotice] = useState('');
    const beatRef = useRef(null);
    const askRef = useRef(ask);
    const latestRef = useRef(latest);
    const waiterRef = useRef(null);
    const baseIdRef = useRef(null);
    const streamRef = useRef(null);
    askRef.current = ask;
    latestRef.current = latest;

    useEffect(() => {
        const id = setInterval(() => setSecs(s => s + 1), 1000);
        return () => clearInterval(id);
    }, []);

    // A new finished answer arrives through the socket: hand it to the waiting call loop.
    useEffect(() => {
        const l = latest;
        if (waiterRef.current && l && typeof l.chatHistoryId === 'number' && l.response && l.response !== 'LoadingFromAi'
            && l.chatHistoryId !== baseIdRef.current) {
            const resolve = waiterRef.current;
            waiterRef.current = null;
            resolve(l.response);
        }
    }, [latest]);

    useEffect(() => {
        if (streamRef.current) streamRef.current.getAudioTracks().forEach(t => { t.enabled = !muted; });
    }, [muted]);

    useEffect(() => {
        let alive = true;
        let timer;
        let listener = null;
        let player = null;
        const sleep = (ms) => new Promise(res => { timer = setTimeout(res, ms); });
        const beat = () => {
            const el = beatRef.current;
            if (!el) return;
            el.classList.remove('hit');
            el.getBoundingClientRect(); // force reflow so the animation restarts
            el.classList.add('hit');
        };
        const setLast = (who, text) => setLines(prev => {
            const next = prev.slice();
            next[next.length - 1] = { who, text };
            return next;
        });
        const addLine = (who, text) => setLines(prev => [...prev.slice(-1), { who, text }]);

        // ---------- demo mode ----------
        const runDemo = async () => {
            let i = 0;
            while (alive) {
                const step = CALL_SCRIPT[i % CALL_SCRIPT.length];
                if (i % CALL_SCRIPT.length === 0) setLines([]);
                setState(step.who === 'g' ? 't' : 'u');
                await sleep(step.who === 'g' ? 650 : 1100);
                if (!alive) return;
                setState(step.who);
                addLine(step.who, '');
                const words = step.text.split(' ');
                for (let n = 1; n <= words.length; n += 1) {
                    setLast(step.who, words.slice(0, n).join(' '));
                    if (step.who === 'g') beat();
                    await sleep((step.who === 'g' ? 230 : 150) + words[n - 1].length * 24);
                    if (!alive) return;
                }
                await sleep(1100);
                i += 1;
            }
        };

        // ---------- live mode ----------
        const say = async (text) => {
            if (!text || !alive) return;
            rememberReply(text);
            setState('t');
            const res = await voiceApi.speak(text);
            const blob = res && res.data ? res.data : res;
            if (!alive) return;
            const words = text.split(' ');
            addLine('g', '');
            setState('g');
            let shown = 0;
            player = playBlob(blob, (p) => {
                const n = Math.min(words.length, Math.ceil(p * words.length));
                if (n !== shown) { shown = n; setLast('g', words.slice(0, n).join(' ')); beat(); }
            }, VOICE_SPEED);
            await player.promise;
            player = null;
            if (alive) setLast('g', text);
        };
        const getReply = (text) => new Promise((resolve) => {
            baseIdRef.current = latestRef.current ? latestRef.current.chatHistoryId : null;
            waiterRef.current = resolve;
            askRef.current(text);
            setTimeout(() => { if (waiterRef.current === resolve) { waiterRef.current = null; resolve(null); } }, 60000);
        });
        const runLive = async () => {
            resetSmallTalk();
            try {
                streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
            } catch (e) {
                if (alive) setNotice('Allow microphone access in your browser to talk to Genie.');
                return;
            }
            if (!alive) { streamRef.current.getTracks().forEach(t => t.stop()); return; }
            listener = createListener(streamRef.current);
            let fails = 0;
            try {
                await say(`Hi${firstName ? ` ${firstName}` : ''}, I'm Genie. What can I help you with?`);
            } catch (e) {
                if (alive) setNotice('Voice is unavailable right now. You can still type in the chat.');
                return;
            }
            while (alive) {
                try {
                    setState('u');
                    const blob = await listener.listenOnce();
                    if (!alive) return;
                    if (!blob) continue;
                    setState('t');
                    const tr = await voiceApi.transcribe(blob);
                    const { text } = (tr && tr.data ? tr.data : tr) || {};
                    if (!text || !text.trim()) continue;
                    const heard = normalizeCrm(text).trim();
                    // Everyday conversation is answered on the device. A CRM question with a greeting around it is cleaned and sent on.
                    const local = localReply(heard, firstName);
                    if (local && local.ignore) continue;
                    addLine('u', heard);
                    if (local && local.text) {
                        await say(local.text);
                        if (local.end) { onEnd(); return; }
                        fails = 0;
                        continue;
                    }
                    const question = (local && local.passthrough) || heard;
                    const html = await getReply(question);
                    if (!alive) return;
                    await say(html ? toSpokenText(html) : "Sorry, I couldn't get an answer in time. Please try again.");
                    fails = 0;
                } catch (e) {
                    if (!alive) return;
                    fails += 1;
                    if (fails >= 3) { setNotice('Voice is unavailable right now. You can still type in the chat.'); return; }
                    await sleep(1200);
                }
            }
        };

        if (live) runLive(); else runDemo();

        return () => {
            alive = false;
            clearTimeout(timer);
            waiterRef.current = null;
            if (player) player.stop();
            if (listener) listener.close();
            else if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const mm = String(Math.floor(secs / 60)).padStart(2, '0');
    const ss = String(secs % 60).padStart(2, '0');
    const label = state === 'g' ? 'Genie is speaking'
        : state === 'u' ? (muted ? "You're muted" : 'Listening to you')
            : 'Genie is thinking';

    return (
        <div className="genie-call" data-state={notice ? 'idle' : state} role="dialog" aria-label="Call with Genie">
            <div className="genie-call-top">
                <span className="genie-call-tag"><i />{live ? 'Voice call' : 'Voice call preview'}</span>
                <span className="genie-call-time">{mm}:{ss}</span>
            </div>
            <div className="genie-call-stage">
                <div className="genie-call-glow" />
                <div className="genie-call-rings"><i /><i /><i /></div>
                <span className="genie-call-spark">{SPARKLE}</span>
                <span className="genie-call-spark">{SPARKLE}</span>
                <span className="genie-call-spark">{SPARKLE}</span>
                <span className="genie-call-spark">{SPARKLE}</span>
                <div className="genie-call-ground" />
                <div className="genie-call-float">
                    <div className="genie-call-tilt">
                        <div className="genie-call-beat" ref={beatRef}>
                            <img className="genie-call-img" src={require('../../../../static/img/defaultImages/Genie.png')} alt="Genie" draggable={false} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="genie-call-state">
                <span className="genie-call-bars" aria-hidden="true"><i /><i /><i /><i /><i /></span>
                <span>{notice ? 'Call paused' : label}</span>
            </div>
            <div className="genie-call-caps" aria-live="off">
                {notice ? <div className="genie-cap cur"><span className="txt">{notice}</span></div> : lines.map((l, k) => (
                    <div key={`${k}-${l.who}`} className={`genie-cap ${l.who === 'u' ? 'user' : ''} ${k < lines.length - 1 ? 'old' : 'cur'}`}>
                        <small>{l.who === 'g' ? 'Genie' : 'You'}</small>
                        <span className="txt">{l.text}</span>
                    </div>
                ))}
            </div>
            <div className="genie-call-ctrl">
                <button type="button" className="genie-call-mute" aria-pressed={muted} aria-label={muted ? 'Unmute' : 'Mute'} onClick={() => setMuted(m => !m)}>
                    <FeatherIcon icon={muted ? 'mic-off' : 'mic'} size={18} />
                </button>
                <button type="button" className="genie-call-end" onClick={onEnd}>
                    <FeatherIcon icon="phone-off" size={16} /> End call
                </button>
            </div>
        </div>
    );
}

GenieCall.propTypes = {
    onEnd: PropTypes.func.isRequired,
    firstName: PropTypes.string,
    voiceApi: PropTypes.shape({
        transcribe: PropTypes.func,
        speak: PropTypes.func,
    }),
    ask: PropTypes.func.isRequired,
    latest: PropTypes.shape({
        chatHistoryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        response: PropTypes.string,
    }),
};

GenieCall.defaultProps = {
    firstName: '',
    voiceApi: null,
    latest: null,
};

function Filters() {
    const scrollRef = useRef(null);
    const dispatch = useDispatch()
    const localData = useSelector((state) => state.localRedux);
    const chatBot = useSelector((state) => state.chatRedux.chatBot);
    // Set only while a lead/account/deal/contact/case record is open; null everywhere else.
    const currentView = useSelector((state) => state.chatCurrentViewRedux.currentView);
    const ProfileData = localData.profile
    const profile = ProfileData
    const companyProfile = localData.company
    const [inputValue, setInputValue] = useState()
    const [chatList, setChatList] = useState()
    const [confirmPopup, setConfirmPopup] = useState(false)
    const [confirmPopup1, setConfirmPopup1] = useState(false)
    const [deleteId, setDeleteId] = useState()
    const [display, setDisplay] = useState(false)

    // --- new state ---
    const [suggestions, setSuggestions] = useState(FALLBACK_SUGGESTIONS)
    const [selectMode, setSelectMode] = useState(false)
    const [selectedIds, setSelectedIds] = useState([])
    const [previewIds, setPreviewIds] = useState([])
    const [exporting, setExporting] = useState(false)
    const [callOpen, setCallOpen] = useState(false)
    const latestAnswer = useMemo(
        () => [...(chatList || [])].reverse().find(i => typeof i.chatHistoryId === 'number' && i.response && i.response !== 'LoadingFromAi'),
        [chatList]
    )

    const FilterOpen1 = () => {
        dispatch(setChatCall(false));
        setChatList()
        setInputValue("")
        setDisplay(false)
        setSelectMode(false)
        setSelectedIds([])
        setPreviewIds([])
        setCallOpen(false)
    }
    async function AllChatHistory() {
        try {
            const response = await fetchLastchatHistory(companyProfile.companyCode)
            const SortedData = response.sort((a, b) => a.chatHistoryId - b.chatHistoryId);
            setChatList(SortedData)
        }
        catch (error) {
            message.error(error)
        }
    }
    async function loadSuggestions() {
        if (typeof ChatApi.fetchSuggestedQuestions !== 'function') return;
        try {
            const res = await ChatApi.fetchSuggestedQuestions((currentView?.entityType && currentView?.entityId) ? "true" : "false")
            const list = Array.isArray(res) ? res : res?.data
            if (Array.isArray(list) && list.length) setSuggestions(list)
        } catch (e) {
            /* keep fallback chips */
        }
    }
    const fetchAll = async () => {
        await Promise.allSettled([
            AllChatHistory(),
            loadSuggestions(),
        ])
    };
    useEffect(() => {
        if (chatBot) {
            fetchAll();
            setDisplay(false)
        }
    }, [chatBot])
    const handleChange = e => {
        setInputValue(e.target.value)
    };

    const sendQuery = async (text) => {
        const query = (text || '').trim()
        if (!query || display) return
        // Everyday conversation is answered on the device: no rewrite, planner or retrieval call.
        const local = localReply(normalizeCrm(query).trim(), profile?.firstName)
        if (local && local.ignore) { setInputValue(""); return }
        if (local && local.text) {
            const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            setInputValue("")
            rememberReply(local.text)
            setChatList(prev => [...(prev || []), { chatHistoryId: `Local_${Date.now()}`, query, response: `<p>${esc(local.text)}</p>`, isLocal: true }])
            return
        }
        // "Hi Genie, show my hot leads" -> "show my hot leads"
        if (local && local.passthrough) return sendQuery(local.passthrough)
        setDisplay(true)
        setInputValue("")
        setChatList(prev => [...(prev || []), { chatHistoryId: "Temp_1", query, response: "LoadingFromAi" }])
        const payload =
        {
            companyCode: companyProfile.companyCode,
            queryText: query,
            limit: 20,
            // Only send currentView when a record page is open
            ...(currentView?.entityType && currentView?.entityId
                ? { currentView: { entityType: currentView.entityType, entityId: currentView.entityId } }
                : {}),
        }
        try {
            const response = await Userchatsubmit(payload)
            if (response.status === 201) {
                setDisplay(false)
                setInputValue("")
            }
        }
        catch (error) {
            setDisplay(false)
            setInputValue("")
            setChatList(prev => (prev || []).filter(i => i?.chatHistoryId !== "Temp_1"))
            message.error(error)
        }
    }
    const handleSubmit = () => sendQuery(inputValue)
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    }

    useEffect(() => {
        const socket = getSocket();
        const handleNotification = (data) => {
            setChatList(prev => {
                const list = Array.isArray(prev) ? prev : [];
                const originalData = list.filter(
                    item => item?.chatHistoryId !== "Temp_1"
                );
                return [...originalData, data];
            });
        }
        socket.off("chat_update", handleNotification);
        socket.on("chat_update", handleNotification);
        const handleConnect = () => {
            socket.emit("join", profile.userId);
        };
        socket.off("connect", handleConnect);
        socket.on("connect", handleConnect);
        return () => {
            socket.off("chat_update", handleNotification);
            socket.off("connect", handleConnect);
        };
    }, [profile.userId]);
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollToBottom();
        }
    }, [chatList]);
    const OnClickofDelete = (value) => {
        setConfirmPopup(true)
        setDeleteId(value)
    }
    const OnClickofDelete1 = () => {
        setConfirmPopup1(true)
    }
    async function deleteMenu(id) {
        let hideMessage; try {
            hideMessage = message.loading("Loading...", 0);
            const Response = await genieChatDelte(id)
            if (Response.status === 200) {
                setConfirmPopup(false)
                AllChatHistory()
                hideMessage();
                setDisplay(false)
            }
        } catch (error) {
            setDisplay(false)
            if (hideMessage) hideMessage();
            message.error(error)
        }
    }
    async function deleteMenu2() {
        let hideMessage; try {
            hideMessage = message.loading("Loading...", 0);
            const Response = await genieChatClear(companyProfile.companyCode)
            if (Response.status === 200) {
                setConfirmPopup1(false)
                AllChatHistory()
                hideMessage();
                setDisplay(false)
            }
        } catch (error) {
            setDisplay(false)
            if (hideMessage) hideMessage();
            message.error(error)
        }
    }

    // ---------- selection / PDF ----------
    const selectableIds = useMemo(
        () => (chatList || []).filter(i => typeof i.chatHistoryId === 'number' && i.response !== 'LoadingFromAi').map(i => i.chatHistoryId),
        [chatList]
    );
    const toggleSelect = useCallback((id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    }, []);
    const toggleSelectMode = () => {
        if (display) return;
        setSelectMode(prev => !prev);
        setSelectedIds([]);
    };
    const allSelected = selectableIds.length > 0 && selectedIds.length === selectableIds.length;
    const toggleSelectAll = () => setSelectedIds(allSelected ? [] : selectableIds);
    const togglePreview = (id) => setPreviewIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    // Builds the PDF from the response text (no screenshots), so nothing on screen is touched or resized.
    const downloadPdf = () => {
        if (!selectedIds.length || exporting) return;
        setExporting(true);
        try {
            const pdf = new JsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
            const pageW = pdf.internal.pageSize.getWidth();
            const pageH = pdf.internal.pageSize.getHeight();
            const margin = 40;
            const usableW = pageW - margin * 2;
            let y = 0;

            const drawHeader = () => {
                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(14);
                pdf.setTextColor(30, 41, 59);
                pdf.text('Genie - CRM Assistant', margin, 30);
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(9);
                pdf.setTextColor(107, 114, 128);
                pdf.text(`${companyProfile?.companyName || ''}  |  Exported ${new Date().toLocaleString()}`, margin, 44);
                pdf.setDrawColor(229, 231, 235);
                pdf.line(margin, 50, pageW - margin, 50);
                y = 66;
            };
            const need = (h) => {
                if (y + h > pageH - margin) { pdf.addPage(); drawHeader(); }
            };
            const write = (text, { size = 10, bold = false, color = [31, 41, 55], indent = 0, gap = 4 } = {}) => {
                pdf.setFont('helvetica', bold ? 'bold' : 'normal');
                pdf.setFontSize(size);
                pdf.setTextColor(...color);
                const lh = size * 1.4;
                pdf.splitTextToSize(text, usableW - indent).forEach((line) => {
                    need(lh);
                    pdf.text(line, margin + indent, y + size);
                    y += lh;
                });
                y += gap;
            };
            const writeTable = ({ rows, header }) => {
                if (!rows.length) return;
                const cols = Math.max(...rows.map(r => r.length));
                const colW = usableW / cols;
                pdf.setFontSize(9);
                rows.forEach((row, ri) => {
                    const isHead = header && ri === 0;
                    pdf.setFont('helvetica', isHead ? 'bold' : 'normal');
                    const cells = Array.from({ length: cols }, (_, c) => pdf.splitTextToSize(row[c] || '', colW - 10));
                    const rowH = Math.max(...cells.map(c => c.length)) * 12 + 8;
                    need(rowH);
                    cells.forEach((lines, c) => {
                        const x = margin + c * colW;
                        pdf.setDrawColor(209, 213, 219);
                        if (isHead) { pdf.setFillColor(243, 244, 246); pdf.rect(x, y, colW, rowH, 'FD'); }
                        else pdf.rect(x, y, colW, rowH);
                        pdf.setTextColor(31, 41, 55);
                        lines.forEach((line, li) => pdf.text(line, x + 5, y + 14 + li * 12));
                    });
                    y += rowH;
                });
                y += 8;
            };

            drawHeader();
            const ordered = selectableIds.filter(id => selectedIds.includes(id));
            ordered.forEach((id) => {
                const item = (chatList || []).find(i => i.chatHistoryId === id);
                if (!item) return;
                if (item.query) write(`You: ${item.query}`, { size: 11, bold: true, color: [37, 99, 235], gap: 6 });
                write('Genie:', { size: 9, bold: true, color: [107, 114, 128], gap: 2 });
                blocksFromHtml(item.response).forEach((b) => {
                    if (b.type === 'h') write(b.text, { size: b.level <= 2 ? 13 : 11, bold: true, gap: 4 });
                    else if (b.type === 'li') write(b.text, { indent: 12, gap: 2 });
                    else if (b.type === 'table') writeTable(b);
                    else write(b.text);
                });
                y += 10;
                need(20);
                pdf.setDrawColor(229, 231, 235);
                pdf.line(margin, y, pageW - margin, y);
                y += 14;
            });

            const pages = pdf.getNumberOfPages();
            for (let p = 1; p <= pages; p += 1) {
                pdf.setPage(p);
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(8);
                pdf.setTextColor(156, 163, 175);
                pdf.text(`Page ${p} of ${pages}`, pageW - margin - 50, pageH - 16);
            }
            pdf.save(`genie-chat-${new Date().toISOString().slice(0, 10)}.pdf`);
            message.success('PDF downloaded');
            setSelectMode(false);
            setSelectedIds([]);
        } catch (e) {
            message.error('Could not generate the PDF');
        } finally {
            setExporting(false);
        }
    };

    // ---------- render helpers ----------
    const WelcomeCard = () => (
        <div className="genie-welcome">
            <img src={require('../../../../static/img/defaultImages/GenieIcon.png')} alt="Genie" className="genie-welcome-logo" />
            <h3 className="genie-welcome-title">{greeting()}{profile?.firstName ? `, ${profile.firstName}` : ''}</h3>
            <p className="genie-welcome-sub">
                I&apos;m Genie, your sales assistant. Ask me about your leads, contacts, accounts, deals and cases,
                or let me help you spot cross-sell and upsell opportunities.
            </p>
        </div>
    );

    const SuggestionChips = () => (
        <div className="genie-suggest">
            <div className="genie-suggest-label">Suggested questions</div>
            <div className="genie-suggest-list">
                {suggestions.map(s => (
                    <button
                        type="button"
                        key={s.id || s.question}
                        className="genie-chip"
                        disabled={display || selectMode}
                        style={selectMode ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                        onClick={() => sendQuery(s.question)}
                    >
                        {s.category && <span className="genie-chip-cat">{s.category}</span>}
                        <span>{s.question}</span>
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <>
            <Drawer
                className="Drawer-Header chat-Drawer genie-pro"
                bodyStyle={{ padding: 0, display: "flex", flexDirection: "column", height: "100%" }}
                title={<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "0px 9px 0px 10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                        <img
                            src={require('../../../../static/img/defaultImages/GenieIcon.png')} alt="Genie" className="genie-icon"
                            style={{ width: 40, height: 40, borderRadius: "50%", background: "white", padding: 3 }}
                        />
                        <div>
                            <div style={{ fontSize: 18, fontWeight: 500, color: "white" }}>Genie</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <span className="botActive-dot" />
                                <span style={{ fontSize: 12, color: "#adb4d2" }}>Active now</span>
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px"
                        }}
                    >
                        {/* Call Genie */}
                        <div
                            className={`Hover-Refresh2 back-hover1 able-style genie-hdr-btn ${callOpen ? 'active' : ''}`}
                            style={{ color: "white", ...(selectMode ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }}
                            role="button"
                            tabIndex={0}
                            title="Call Genie"
                            onClick={() => { if (!display && !selectMode) setCallOpen(true) }}
                            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !display && !selectMode) setCallOpen(true); }}
                        >
                            <FeatherIcon icon="phone" size={20} />
                        </div>
                        {/* Select / export */}
                        {selectableIds.length > 0 && (
                            <div
                                className={`Hover-Refresh2 back-hover1 able-style genie-hdr-btn ${selectMode ? 'active' : ''}`}
                                style={{ color: "white" }}
                                role="button"
                                tabIndex={0}
                                title={selectMode ? 'Cancel selection' : 'Select responses'}
                                onClick={toggleSelectMode}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleSelectMode(); }}
                            >
                                <FeatherIcon icon={selectMode ? 'x-square' : 'check-square'} size={20} />
                            </div>
                        )}
                        {/* Clear Chat Button */}
                        {chatList?.length > 0 ? <div
                            className="Hover-Refresh2 back-hover1 able-style"
                            style={{ color: "white", ...(selectMode ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }}
                            role="button"
                            tabIndex={0}
                            title="Clear Chat"
                            onClick={() => {
                                if (!display && !selectMode) { OnClickofDelete1() }
                            }}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    if (!display && !selectMode) { OnClickofDelete1() }
                                }
                            }}
                        >
                            <ClearChatIcon width={24} height={24} marginRight={5} />
                        </div> : <></>}
                        {/* Close Button */}
                        <div
                            className="Hover-Refresh2 back-hover1 able-style"
                            style={{ color: "white", ...(selectMode ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }}
                            role="button"
                            tabIndex={0}
                            title="Close Chat"
                            // onClick={() => { if (!display) { FilterOpen1() } }}
                            onClick={() => { if (!selectMode) FilterOpen1() }}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    // if (!display) { FilterOpen1(); }
                                    if (!selectMode) FilterOpen1()
                                }
                            }}
                        >
                            <CancelIcon width={30} height={30} />
                        </div>
                    </div>
                </div>}
                closable={false}
                placement="right"
                open={chatBot}
                width="50%"
                footer={[
                    <Footer key="footer">
                        {selectMode ? (
                            <div className="genie-selectbar">
                                <label className="genie-check-all">
                                    <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                                    <span>Select all ({selectedIds.length}/{selectableIds.length})</span>
                                </label>
                                <div className="genie-selectbar-actions">
                                    <button type="button" className="genie-btn-ghost" onClick={toggleSelectMode}>Cancel</button>
                                    <Button
                                        className="gradient-button"
                                        onClick={downloadPdf}
                                        disabled={!selectedIds.length || exporting}
                                    >
                                        <FeatherIcon icon="download" size={16} /> <span style={{ marginLeft: 6 }}>Download PDF</span>
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="chatbox-reply-form d-flex">
                                <div className="chatbox-reply-input">
                                    <input
                                        onChange={handleChange}
                                        onKeyDown={handleKeyDown}
                                        placeholder="Ask about leads, contacts, deals or accounts..."
                                        name="chat"
                                        id="chat"
                                        style={{ width: '100%' }}
                                        value={inputValue}
                                        disabled={display}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="chatbox-reply-action d-flex">
                                    <Button onClick={handleSubmit} className="gradient-button" disabled={display || !inputValue || inputValue.trim() === ""}>
                                        <FeatherIcon icon="send" size={20} />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Footer>
                ]}
            >
                {callOpen && <GenieCall onEnd={() => setCallOpen(false)} firstName={profile?.firstName} voiceApi={VOICE_API} ask={sendQuery} latest={latestAnswer} />}
                <Scrollbars autoHide autoHideTimeout={500} autoHideDuration={200} ref={scrollRef}>
                    <div className="genie-body" style={{ padding: "20px 30px" }}>
                        {(chatList === null || chatList === undefined) ? (
                            <Skeleton
                                paragraph={{ rows: 20, width: "100%" }}
                                active
                                title={false}
                            />) : (
                            <>
                                {/* Welcome message is always the first thing shown */}
                                <WelcomeCard />
                                <SuggestionChips />

                                {chatList.map((item, index) => {
                                    const id = item.chatHistoryId;
                                    const selectable = typeof id === 'number' && item.response !== 'LoadingFromAi';
                                    const selected = selectedIds.includes(id);
                                    const previewing = previewIds.includes(id);
                                    const showCheck = selectMode && selectable;
                                    return (
                                        <div
                                            key={index}
                                            role="presentation"
                                            // No 'selectable' / 'selected' classes on purpose: select mode only toggles the checkbox,
                                            // so there is no border, background or width change and the layout never shifts.
                                            className="genie-turn"
                                            style={{
                                                position: 'relative',
                                                cursor: showCheck ? 'pointer' : undefined,
                                                // Highlight uses background + box-shadow only (no border/padding/width), so the layout never shifts.
                                                ...(showCheck && selected
                                                    ? { background: '#eef4ff', borderRadius: 12, boxShadow: '0 0 0 1px #8b9cf7' }
                                                    : {}),
                                            }}
                                            onClick={() => { if (showCheck) toggleSelect(id); }}
                                        >
                                            {showCheck && (
                                                <input
                                                    type="checkbox"
                                                    style={{ position: 'absolute', left: -22, top: 8, margin: 0, cursor: 'pointer' }}
                                                    checked={selected}
                                                    onChange={() => toggleSelect(id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    aria-label="Select response"
                                                />
                                            )}
                                            <div className="genie-turn-content">
                                                {item.query && (
                                                    <div className="chatBot-message chatBot-right">
                                                        <div className="chatbot-row">
                                                            <div className="bubble userchatBot-bubble user-bubble-wrapper">
                                                                {item.query}
                                                                {typeof item.chatHistoryId === "number" && !selectMode && (
                                                                    <span
                                                                        role="button"
                                                                        tabIndex={0}
                                                                        aria-label="Delete chat"
                                                                        className="chat-delete-icon fetcherDelete-Icon"
                                                                        onClick={() => OnClickofDelete(item.chatHistoryId)}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                                OnClickofDelete(item.chatHistoryId);
                                                                            }
                                                                        }} >
                                                                        <FeatherIcon icon="trash-2" size={14} />
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {(profile?.profilePic && profile.profilePic !== "") ?
                                                                <img
                                                                    src={profile?.profilePic}
                                                                    alt="User"
                                                                    className="chatbot-avatar"
                                                                /> :
                                                                <span className='details_span_Tag UserIcon chatbot-avatar' style={{ flexShrink: 0, }}>
                                                                    {profile?.firstName?.slice(0, 1).toUpperCase()}
                                                                </span>
                                                            }
                                                        </div>
                                                    </div>
                                                )}
                                                {item.response === "LoadingFromAi" ? (
                                                    <div className="bubble bot-bubble loadingBot-bubble">
                                                        <span className="dotBot" />
                                                        <span className="dotBot" />
                                                        <span className="dotBot" />
                                                        <span className="dotBot" />
                                                        <span className="dotBot" />
                                                    </div>
                                                ) : (
                                                    <div className="chatBot-message chatBot-left">
                                                        <div className="chatbot-row">
                                                            <img
                                                                src={require('../../../../static/img/defaultImages/GenieIcon.png')} alt="Genie" className="chatbot-avatar1"
                                                            />
                                                            <div className="bubble chatbot-bubble genie-bot-bubble">
                                                                {parse(cleanHtml(item.response))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* HTML preview container */}
                                            {selectable && (
                                                <div className="genie-actions" role="presentation" onClick={(e) => e.stopPropagation()}>
                                                    <Tooltip title={previewing ? 'Hide preview' : 'Preview as formatted page'}>
                                                        <button type="button" className="genie-action-btn" onClick={() => togglePreview(id)}>
                                                            <FeatherIcon icon={previewing ? 'eye-off' : 'eye'} size={13} />
                                                            <span>{previewing ? 'Hide preview' : 'Preview'}</span>
                                                        </button>
                                                    </Tooltip>
                                                </div>
                                            )}
                                            {selectable && previewing && (
                                                <div className="genie-preview" role="presentation" onClick={(e) => e.stopPropagation()}>
                                                    <div className="genie-preview-bar">
                                                        <span className="genie-preview-dots"><i /><i /><i /></span>
                                                        <span className="genie-preview-title">Preview</span>
                                                    </div>
                                                    <iframe
                                                        title={`preview-${id}`}
                                                        sandbox=""
                                                        className="genie-preview-frame"
                                                        srcDoc={`<!doctype html><html><head><meta charset="utf-8">${PREVIEW_STYLES}</head><body>${cleanHtml(item.response)}</body></html>`}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                            </>
                        )}
                    </div>
                </Scrollbars>
            </Drawer >
            {confirmPopup && <ConfirmationPopup confirmPopup={confirmPopup} setConfirmPopup={setConfirmPopup} GetCallback={deleteMenu} deleteId={deleteId} page="Delete" />}
            {confirmPopup1 && <ConfirmationPopup confirmPopup={confirmPopup1} setConfirmPopup={setConfirmPopup1} GetCallback={deleteMenu2} page="Clear" />}

        </>
    );
}

Filters.propTypes = {
};

export default Filters;